import { useNotificationChoices } from "@features/ux/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";
import { Button } from "@shared/ui/button";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  usePostBulkNotification,
  useRecipientLists,
  useTemplates,
} from "../hooks";
import { logEvent } from "@shared/analytics/analytics";

function BulkMessage() {
  const remoteUtilityId = useRemoteUtilityId();

  const [formState, setFormState] = useState({
    audience: "",
    recipient: "",
    channel: "",
    format: "Plain Text",
    cc: "", // Keep separate
    bcc: "", // Add BCC field
    subject: "",
    message: "",
    html: "",
    richText: "",
    templateType: "default",
    template: "",
    scheduleForLater: false,
    scheduledDate: "",
    scheduledTime: "",
  });

  const { data: channelTypes } = useNotificationChoices("channel_type");
  const channelTypeChoice = mapToLabelValue(channelTypes);

  const { data: audienceType } = useNotificationChoices("recipient_type");
  const audienceTypeChoice = mapToLabelValue(audienceType);

  const { mutate: sendBulkNotification, isPending } = usePostBulkNotification();

  const { data: templates } = useTemplates({
    channelType: formState.channel,
    remoteUtilityId,
  });

  const templateList = mapToLabelValue(templates, "templateName", "id");

  const { data: recipients } = useRecipientLists({
    recipientType: formState.audience,
    remoteUtilityId,
  });
  const filteredRecipientList = recipients?.result.filter(
    (item) => item.isActive == true && item.isDraft == false
  );

  const recipientList = mapToLabelValue(filteredRecipientList, "name", "id");

  const fields: FormField[] = useMemo(
    () => [
      {
        group: "Recipient Selection",
        groupOrder: 1,
        name: "audience",
        type: "select",
        label: "Audience Type",
        options: audienceTypeChoice,
        required: true,
        placeholder: "Choose Audience Type",
      },
      {
        group: "Recipient Selection",
        groupOrder: 1,
        name: "recipient",
        type: "select",
        label: "Recipient List",
        options: recipientList,
        placeholder: "Select a list",
        required: true,
        helperText: "Select a recipient list for your bulk message",
      },
      {
        group: "Message Configuration",
        groupOrder: 2,
        name: "channel",
        type: "select",
        options: channelTypeChoice,
        label: "Channel Type",
        placeholder: "Select a channel",
        helperText: "Choose how you want to deliver these messages.",
        required: true,
      },
      {
        group: "Message Configuration",
        groupOrder: 2,
        name: "format",
        type: "select",
        label: "Format",
        defaultValue: "Plain Text",
        options: [
          { label: "Plain Text", value: "Plain Text" },
          { label: "Rich Text", value: "Rich Text" },
          { label: "Html", value: "html" },
        ],
        placeholder: "Select format",
      },
      // Replace the single cc field with separate CC and BCC fields
      {
        group: "Message Configuration",
        groupOrder: 2,
        name: "cc",
        type: "text",
        label: "CC (Carbon Copy)",
        placeholder: "Enter email addresses separated by commas",
        className: "col-span-1", // Changed from col-span-2
        required: false, // Changed from required
        showWhen: () => formState.channel === "EMAIL",
        helperText: "Add email addresses for CC (comma-separated)",
      },
      {
        group: "Message Configuration",
        groupOrder: 2,
        name: "bcc",
        type: "text",
        label: "BCC (Blind Carbon Copy)",
        placeholder: "Enter email addresses separated by commas",
        className: "col-span-1", // Changed from col-span-2
        required: false,
        showWhen: () => formState.channel === "EMAIL",
        helperText: "Add email addresses for BCC (comma-separated)",
      },
      {
        group: "Message Configuration",
        groupOrder: 2,
        name: "subject",
        type: "text",
        label: "Subject",
        placeholder: "Write a subject",
        className: "col-span-2",
        required: formState.channel === "EMAIL",
        showWhen: () => formState.channel === "EMAIL",
      },
      {
        group: "Content Type",
        groupOrder: 3,
        name: "templateType",
        type: "radio",
        options: [
          { value: "default", label: "Use Default Template" },
          { value: "custom", label: "Custom Message" },
        ],
        defaultValue: "default",
        className: "col-span-2 w-full flex flex-col justify-between",
      },
      {
        group: "Content Type",
        groupOrder: 3,
        name: "template",
        type: "select",
        label: "Select Template",
        required: formState.templateType === "default",
        placeholder: "Select a Template",
        options: templateList,
        className: "col-span-2",
        showWhen: () => formState.templateType === "default",
      },
      {
        group: "Content Type",
        groupOrder: 3,
        name: "message",
        type: "textarea",
        label: "Message",
        placeholder: "Type your message here...",
        rows: 10,
        className: "col-span-2",
        required: formState.format === "Plain Text",
        showWhen: () => formState.format === "Plain Text",
      },
      {
        group: "Content Type",
        groupOrder: 3,
        name: "richText",
        type: "rich-text",
        label: "Message",
        rows: 10,
        className: "col-span-2",
        required: formState.format === "Rich Text",
        showWhen: () => formState.format === "Rich Text",
      },
      {
        group: "Content Type",
        groupOrder: 3,
        name: "html",
        type: "html-text",
        label: "Message",
        rows: 10,
        className: "col-span-2",
        required: formState.format === "html",
        showWhen: () => formState.format === "html",
      },
      {
        group: "Delivery Options",
        groupOrder: 4,
        name: "scheduleForLater",
        type: "checkbox",
        label: "Schedule for later",
        className: "col-span-2",
      },
      {
        group: "Delivery Options",
        groupOrder: 4,
        name: "scheduledDate",
        type: "datepicker",
        label: "Scheduled Date",
        placeholder: "Pick a date",
        required: formState.scheduleForLater === true,
        showWhen: () => formState.scheduleForLater === true,
        className: "col-span-1",
        customValidation: (value: any) => {
          if (!value) return true; // Allow empty if not required

          const selectedDate = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          if (selectedDate < today) {
            return "Please select a date from today onwards";
          }
          return true;
        },
      },
      {
        group: "Delivery Options",
        groupOrder: 4,
        name: "scheduledTime",
        type: "time",
        label: "Scheduled Time",
        placeholder: "Select time",
        required: formState.scheduleForLater === true,
        showWhen: () => formState.scheduleForLater === true,
        className: "col-span-1",
      },
    ],
    [
      formState,
      audienceTypeChoice,
      recipientList,
      channelTypeChoice,
      templateList,
    ]
  );

  const config: FormConfig = {
    fields,
    showResetButton: false,
    showSubmitButton: false,
    resetOnSubmit: false,
    layout: "grid",
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
      buttonContainer: "hidden",
    },
  };

  const form = FormService.createForm(config);

  const handleSubmit = (data: any) => {
    let scheduledDateTime = null;

    // Combine date and time if both are provided
    if (data.scheduleForLater && data.scheduledDate && data.scheduledTime) {
      scheduledDateTime = `${data.scheduledDate}T${data.scheduledTime}:00`;
    } else if (data.scheduleForLater && data.scheduledDate) {
      scheduledDateTime = data.scheduledDate;
    }

    const payload: any = {
      remote_utility_id: remoteUtilityId,
      channel_type: data.channel,
      list_id: data.recipient,
      body: data.message ?? data.richText ?? data.html,
      ...(scheduledDateTime && { schedule_date: scheduledDateTime }),
    };

    if (data.template) {
      payload.template_id = data.template;
    }

    if (data.subject) payload.subject = data.subject;

    // Add CC if provided
    if (data.cc) {
      payload.cc = data.cc
        .split(",")
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);
    }

    // Add BCC if provided
    if (data.bcc) {
      payload.bcc = data.bcc
        .split(",")
        .map((email: string) => email.trim())
        .filter((email: string) => email.length > 0);
    }

    // Send the notification and reset form only on success
    sendBulkNotification(payload, {
      onSuccess: () => {
        logEvent("Bulk Message Sent", {
          channel_type: data.channel,
          audience: data.audience
        })
        handleReset();
      },
    });
  };

  const handleReset = () => {
    const resetValues = {
      audience: "",
      recipient: "",
      channel: "",
      format: "",
      cc: "",
      bcc: "", // Add BCC to reset
      subject: "",
      templateType: "default",
      template: "",
      message: "",
      html: "",
      richText: "",
      scheduleForLater: false,
      scheduledDate: "",
      scheduledTime: "",
    };
    setFormState(resetValues);
    form.reset(resetValues);
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
     logEvent(`Bulk Messaging ${fieldName} Field`, {
          field: fieldName,
          value: value,
        })
  };

  // Custom footer component similar to UtilityAddForm
  const renderCustomFooter = () => (
    <div className="flex justify-between gap-3 mt-6 pt-4 border-t">
      <Button
        type="button"
        variant="outline"
        onClick={handleReset}
        disabled={isPending}
      >
        Reset Form
      </Button>
      <Button
        type="submit"
        disabled={isPending}
        onClick={form.handleSubmit(handleSubmit)}
      >
        {isPending ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : formState.scheduleForLater === true ? (
          "Schedule Message"
        ) : (
          "Send"
        )}
      </Button>
    </div>
  );

  useEffect(() => {
    if (!formState.template || !Array.isArray(templates)) return;

    const selected = templates.find((t) => t.id === Number(formState.template));
    const message = selected?.content?.body;
    if (!message) return;

    form.setValue("message", message);
    form.setValue("html", message);
    form.setValue("richText", message);
  }, [formState.template, templates]);

  return (
    <div className="bg-white">
      <div className="mb-8">
        <p className="text-gray-600">
          Send messages to multiple recipients by uploading a file or selecting
          a predefined list.
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
        <DynamicForm
          fields={fields}
          form={form}
          onSubmit={handleSubmit}
          onReset={handleReset}
          onFieldChange={handleFieldChange}
          config={config}
          gridColumns={2}
          responsiveColumns={{ sm: 1, md: 2 }}
          className="messaging-form"
          loading={isPending}
          renderCustomFooter={renderCustomFooter}
        />
      </div>
    </div>
  );
}

export default BulkMessage;
